import { useMemo, useState } from "react";
import type { LifelistEntry } from "../lib/types";
import {
  ENDEMIC_REGIONS,
  detectRegions,
  scoreEndemics,
  type EndemicScore,
} from "../../shared/endemics";
import { CheckIcon } from "../ui";
import { logEvent } from "../lib/events";

// Regional endemic scoreboards. Renders one card per region the user
// has activity in — Hawaii for now; more regions grow the shared
// list. A user with zero activity in a region doesn't see its card,
// so the scoreboard never starts at 0/22 for someone who hasn't been.
//
// Each card shows the running score, a subtle progress bar, and a
// disclosure with the full checklist (seen ticks first, then the
// hunting list). Missing species are the point — this is the "what
// to look for next" surface, not just a self-congratulation panel.

export function EndemicsCard({
  lifelist,
  beachRegionTags,
  onOpenSpecies,
}: {
  lifelist: LifelistEntry[];
  // Region strings from the user's trip beaches — helps detect
  // "user has been to Hawaii" even before they've logged a species
  // there.
  beachRegionTags: string[];
  // Called when the user taps a species they've seen — opens the
  // usual SpeciesDetail modal via the parent, since the endemic
  // card doesn't own that modal state.
  onOpenSpecies?: (taxonId: number) => void;
}) {
  const activeRegions = useMemo(
    () =>
      detectRegions(
        lifelist.map((e) => e.location_name || ""),
        beachRegionTags,
      ),
    [lifelist, beachRegionTags],
  );

  const scores = useMemo(() => {
    return ENDEMIC_REGIONS.filter((r) => activeRegions.includes(r.key)).map(
      (r) => scoreEndemics(r, lifelist),
    );
  }, [activeRegions, lifelist]);

  if (scores.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      {scores.map((s) => (
        <RegionCard
          key={s.region.key}
          score={s}
          onOpenSpecies={onOpenSpecies}
        />
      ))}
    </div>
  );
}

function RegionCard({
  score,
  onOpenSpecies,
}: {
  score: EndemicScore;
  onOpenSpecies?: (taxonId: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const total = score.region.species.length;
  const seenCount = score.seen.length;
  const pct = total === 0 ? 0 : Math.round((seenCount / total) * 100);

  return (
    <section className="card p-4">
      <button
        type="button"
        onClick={() => {
          const next = !open;
          setOpen(next);
          logEvent("endemics.toggle", {
            region: score.region.key,
            open: next,
            seen: seenCount,
            total,
          });
        }}
        className="flex w-full items-baseline justify-between gap-3 text-left min-h-[44px]"
        aria-expanded={open}
      >
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink/60">
            Endemics
          </p>
          <h2 className="mt-0.5 font-display text-lg font-bold text-ink">
            {score.region.label}
          </h2>
        </div>
        <div className="shrink-0 text-right">
          <p className="font-display text-2xl font-bold tabular-nums text-ink">
            {seenCount}
            <span className="ml-1 text-base font-normal text-ink/50">
              / {total}
            </span>
          </p>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-aqua-deep">
            {open ? "Hide list" : "Show list"} · {pct}%
          </p>
        </div>
      </button>
      <div
        className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-ink/10"
        aria-hidden
      >
        <div
          className="h-full rounded-full bg-grad-cta"
          style={{ width: `${pct}%` }}
        />
      </div>
      {open && (
        <div className="mt-4 space-y-4">
          {score.seen.length > 0 && (
            <div>
              <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink/60">
                You've seen
              </h3>
              <ul className="mt-2 flex flex-col gap-1.5">
                {score.seen.map((s) => {
                  const tid = score.seenTaxonIds.get(s.scientific_name);
                  const inner = (
                    <>
                      <CheckIcon className="h-4 w-4 flex-none text-aqua-deep" />
                      <span className="flex-1 text-sm text-ink">
                        {s.common_name}
                      </span>
                      <span className="truncate text-xs italic text-ink/50">
                        {s.scientific_name}
                      </span>
                    </>
                  );
                  return (
                    <li key={s.scientific_name}>
                      {onOpenSpecies && tid != null ? (
                        <button
                          type="button"
                          onClick={() => {
                            logEvent("endemics.species_tap", {
                              region: score.region.key,
                              scientific_name: s.scientific_name,
                              seen: true,
                            });
                            onOpenSpecies(tid);
                          }}
                          className="flex min-h-[44px] w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition hover:bg-ink/[0.03]"
                        >
                          {inner}
                          <span aria-hidden className="ml-1 text-ink/30">
                            ›
                          </span>
                        </button>
                      ) : (
                        <div className="flex min-h-[44px] items-center gap-2 px-2 py-2">
                          {inner}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {score.missing.length > 0 && (
            <div>
              <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink/60">
                Still to find · {score.missing.length}
              </h3>
              <ul className="mt-2 flex flex-col gap-1.5">
                {score.missing.map((s) => (
                  <li
                    key={s.scientific_name}
                    className="flex min-h-[44px] items-baseline gap-2 px-2 py-2"
                  >
                    <span
                      aria-hidden
                      className="mt-1 h-3.5 w-3.5 flex-none rounded-full border border-ink/25"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-sm text-ink">
                          {s.common_name}
                        </span>
                        <span className="truncate text-xs italic text-ink/45">
                          {s.scientific_name}
                        </span>
                      </div>
                      {s.hint && (
                        <p className="text-xs text-ink/55">{s.hint}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
